import { FC, ReactElement, useMemo, useState, useEffect } from 'react'
import styled from 'styled-components'
import * as d3 from 'd3'
import { useSize } from './useSize'
import { XTicks } from './XTicks'
import { YTicks } from './YTicks'
import { Tooltip } from './Tooltip'

interface ChartProps {
  title: string
  legend?: string
  locale: 'de' | 'fr'
  type: string
  data: any
  colorMode: 'light' | 'dark'
  height?: number
  lineColor?: string
  areaColor?: string
  xTicks?: xTickDataType
  yTicks?: yTicksDataType
  xDomain: Date[]
  yDomain: number[]
}

const Chart: FC<ChartProps> = ({
  title,
  legend,
  locale,
  type,
  data,
  colorMode = 'dark',
  height = 150,
  lineColor = '#888',
  areaColor = '#eee',
  xTicks,
  yTicks,
  xDomain,
  yDomain,
}): ReactElement => {
  data = data.filter((f: DataPoint) => f.v)
  const [container, size] = useSize()
  const [plotArea, setPlotArea] = useState({
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  })
  const [ttData, setTtData] = useState<ttDataType | undefined>()
  const createTooltip = (e: any) => {
    let xgraph = xAxisScale.invert(Math.floor(e.nativeEvent.layerX))
    let date = new Date(xgraph).toISOString().split('T')[0]
    let els = data.filter((f: DataPoint) => f.d === date)
    if (els.length === 0) return null

    const x = xAxisScale(new Date(date))
    let ttdata = {
      pos: {
        x: x,
        anchor: (x > size.width / 2 ? 'end' : 'start') as AnchorType,
      },
      data: els,
      suffix: yTicks && yTicks.suffix ? yTicks.suffix : null,
    }
    setTtData(ttdata)
  }
  const cancelTooltip = () => setTtData(undefined)

  const theme = {
    colorMode: colorMode,
    textColor: colorMode === 'dark' ? '#e9e9e9' : '#202346',
    lineColor: colorMode === 'dark' ? '#e9e9e9' : '#d4d4d4',
    labelColor: colorMode === 'dark' ? '#efefef' : '#474a66',
    strokeColor: colorMode === 'dark' ? '#efefef' : '#121212',
  }

  const gutterTop = 0
  const gutterRight = 0
  const gutterLeft = 0
  const gutterBottom = 25
  useEffect(
    () =>
      setPlotArea({
        top: gutterTop,
        right: size.width - gutterRight,
        left: gutterLeft,
        bottom: size.height - gutterBottom,
      }),
    [size, gutterLeft, gutterRight]
  )

  const xAxisScale = useMemo(
    () =>
      d3.scaleLinear().domain(xDomain).range([plotArea.left, plotArea.right]),
    [plotArea, xDomain]
  )

  const yAxisScale = useMemo(
    () =>
      d3.scaleLinear().domain(yDomain).range([plotArea.bottom, plotArea.top]),
    [plotArea, yDomain]
  )

  const xFunction = useMemo(
    () => (d: any, i: number) => {
      return xAxisScale(new Date(d.d))
    },
    [xAxisScale]
  )

  const yFunction = useMemo(() => (d: any) => yAxisScale(d.v), [yAxisScale])

  const lineFunction = useMemo(
    () => d3.line().x(xFunction).y(yFunction),
    [xFunction, yFunction]
  )

  return (
    <>
      <Box>
        <ChartTitle>{title}</ChartTitle>
        {legend && <ChartLegend>{legend}</ChartLegend>}
        <ChartContainer
          ref={container}
          style={{
            height: height,
          }}
        >
          <svg
            style={{ width: size.width, height: size.height }}
            onTouchMove={createTooltip}
            onMouseMove={createTooltip}
            onMouseLeave={cancelTooltip}
            onTouchCancel={cancelTooltip}
          >
            {data.length > 0 && (
              <>
                <path
                  d={lineFunction(data) as string}
                  stroke={lineColor}
                  fill={areaColor}
                />
              </>
            )}
            <TicksGroup theme={theme}>
              {yTicks && <YTicks data={yTicks} x={xAxisScale} y={yAxisScale} />}
              {xTicks && (
                <XTicks
                  data={xTicks}
                  locale={locale}
                  x={xAxisScale}
                  y={yAxisScale}
                  hideLabels={!!ttData}
                />
              )}
            </TicksGroup>
            <TooltipGroup theme={theme}>
              {ttData && (
                <Tooltip data={ttData} locale={locale} y={yAxisScale} />
              )}
            </TooltipGroup>
          </svg>
        </ChartContainer>
      </Box>
    </>
  )
}

const Box = styled.div`
  display: flex;
  padding: 6px;
  flex: 0 0 calc(50% - 22px);

  // TODO
  @media screen and (max-width: 600px) {
    flex: 0 0 100%;
  }
  flex-wrap: wrap;
  grid-gap: 5px;
  gap: 5px;
  font-family: var(--font-sans-serif);
`
const ChartTitle = styled.h4`
  margin: 1rem 0 0 0;
  padding-bottom: 0;
  width: 100%;
`
const ChartLegend = styled.div`
  width: 100%;
  font-size: 1.4rem;
`

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  font-size: 1rem;
  // outline: 1px solid green; // <- for visual debugging
`
const TicksGroup = styled.g`
  text {
    fill: ${(props) => props.theme.labelColor};
    font-weight: 200;
  }
  line {
    stroke-width: 0.5pt;
    stroke: ${(props) => props.theme.strokeColor};
  }
`

const TooltipGroup = styled.g`
  text {
    stroke: ${(props) => props.theme.labelColor};
    font-weight: 200;
  }
  line {
    stroke: ${(props) => props.theme.strokeColor};
    stroke-width: 0.5pt;
  }
`

export default Chart
